import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Save, Pencil, RefreshCw, BadgePercent, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/adminApi";

const TIER_LABELS = {
  starter: "Starter / Başlangıç",
  pro: "Pro / Profesyonel",
  premium: "Premium",
  growth: "Growth / Büyüme",
  scale: "Scale / Ölçek",
};

const fmt = (n) => (Number(n) || 0).toLocaleString("tr-TR");

/**
 * /admin/pricing — one screen to update every package price (all services +
 * Postun Hazır) without digging into each service's edit page.
 * Saves the full service document via PUT /admin/services/{slug} so nothing
 * else (content, translations, features) is touched.
 */
export default function AdminPricingPage() {
  const [services, setServices] = useState([]);
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/services");
      const rows = (data || []).filter((s) => Array.isArray(s.pricing));
      setServices(rows);
      setOriginal(Object.fromEntries(rows.map((s) => [s.slug, JSON.stringify(s.pricing)])));
    } catch (e) {
      toast.error("Fiyatlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setTier = (slug, idx, patch) => {
    setServices((list) => list.map((s) => {
      if (s.slug !== slug) return s;
      const pricing = s.pricing.map((t, i) => (i === idx ? { ...t, ...patch } : t));
      return { ...s, pricing };
    }));
  };

  const setPopular = (slug, idx) => {
    setServices((list) => list.map((s) => {
      if (s.slug !== slug) return s;
      return { ...s, pricing: s.pricing.map((t, i) => ({ ...t, popular: i === idx })) };
    }));
  };

  const isDirty = (s) => original[s.slug] !== JSON.stringify(s.pricing);

  const save = async (s) => {
    setSavingSlug(s.slug);
    try {
      const payload = { ...s };
      delete payload.created_at;
      delete payload.updated_at;
      const { data } = await api.put(`/admin/services/${s.slug}`, payload);
      setServices((list) => list.map((x) => (x.slug === s.slug ? { ...x, ...data } : x)));
      setOriginal((o) => ({ ...o, [s.slug]: JSON.stringify(data.pricing || s.pricing) }));
      toast.success(`${serviceTitle(s)} fiyatları kaydedildi`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kayıt hatası");
    } finally {
      setSavingSlug(null);
    }
  };

  const saveAll = async () => {
    const dirty = services.filter(isDirty);
    if (!dirty.length) { toast.message("Değişiklik yok"); return; }
    for (const s of dirty) await save(s);
  };

  const serviceTitle = (s) => {
    const c = s.content?.tr || {};
    return [c.hero_title_pre, c.hero_title_accent, c.hero_title_post].filter(Boolean).join(" ") || c.hero_eyebrow || s.slug;
  };

  if (loading) {
    return <div className="p-10 text-ink/50" data-testid="admin-pricing-loading"><Loader2 className="h-5 w-5 animate-spin inline" /> Yükleniyor…</div>;
  }

  const dirtyCount = services.filter(isDirty).length;

  return (
    <div data-testid="admin-pricing-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold inline-flex items-center gap-2">
            <BadgePercent className="h-7 w-7 text-brand-pink" /> Paket Fiyatları
          </h1>
          <p className="mt-1 text-sm text-ink/55">
            Tüm hizmetlerin ve Postun Hazır paketlerinin aylık TRY fiyatları. Değiştir → Kaydet. Site anında güncellenir.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="btn-outline-ink !text-xs !py-2.5 inline-flex items-center gap-2" data-testid="admin-pricing-reload">
            <RefreshCw className="h-4 w-4" /> Yenile
          </button>
          <button type="button" onClick={saveAll} disabled={!dirtyCount || !!savingSlug}
            className="btn-gold !text-xs !py-2.5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-pricing-save-all">
            {savingSlug ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Tümünü Kaydet {dirtyCount ? `(${dirtyCount})` : ""}
          </button>
        </div>
      </div>

      {services.length === 0 && (
        <div className="rounded-2xl bg-white border border-mist p-8 text-center text-ink/55" data-testid="admin-pricing-empty">
          Fiyatlandırılmış hizmet bulunamadı. Ayarlar → "Eksik içerikleri yükle" ile servisleri oluşturabilirsiniz.
        </div>
      )}

      <div className="space-y-5">
        {services.map((s) => (
          <section key={s.slug} className={`rounded-2xl bg-white border p-5 ${isDirty(s) ? "border-brand-pink/60 shadow-[0_0_0_3px_rgba(238,79,122,0.08)]" : "border-mist"}`}
            data-testid={`admin-pricing-service-${s.slug}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div>
                <h2 className="font-display text-xl font-bold">{serviceTitle(s)}</h2>
                <p className="text-xs text-ink/45 uppercase tracking-[0.16em]">{s.slug}</p>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/admin/services/${s.slug}/edit`} className="inline-flex items-center gap-1.5 text-xs text-ink/60 hover:text-ink"
                  data-testid={`admin-pricing-edit-${s.slug}`}>
                  <Pencil className="h-3.5 w-3.5" /> Özellikleri düzenle
                </Link>
                <button type="button" onClick={() => save(s)} disabled={!isDirty(s) || savingSlug === s.slug}
                  className="btn-gold !text-xs !py-2 inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid={`admin-pricing-save-${s.slug}`}>
                  {savingSlug === s.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Kaydet
                </button>
              </div>
            </div>

            {s.pricing.length === 0 ? (
              <p className="text-sm text-ink/50">Bu hizmette paket yok. <Link className="text-brand-pink hover:underline" to={`/admin/services/${s.slug}/edit`}>Paket ekle →</Link></p>
            ) : (
              <div className="grid gap-3 md:grid-cols-3">
                {s.pricing.map((tier, i) => (
                  <div key={`${s.slug}-${i}`} className={`rounded-xl border p-4 ${tier.popular ? "border-brand-pink/50 bg-brand-pink/5" : "border-mist bg-cream/40"}`}
                    data-testid={`admin-pricing-tier-${s.slug}-${tier.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-[0.16em] text-ink/70">{TIER_LABELS[tier.id] || tier.id}</span>
                      <button type="button" onClick={() => setPopular(s.slug, i)}
                        title="En çok tercih edilen olarak işaretle"
                        className={`inline-flex items-center gap-1 text-[0.65rem] font-semibold rounded-full px-2 py-1 transition-colors ${tier.popular ? "bg-brand-pink text-cream" : "bg-mist text-ink/50 hover:text-ink"}`}
                        data-testid={`admin-pricing-popular-${s.slug}-${tier.id}`}>
                        <Star className="h-3 w-3" /> {tier.popular ? "Popüler" : "Popüler yap"}
                      </button>
                    </div>
                    <label className="label-base">Fiyat (TRY / ay)</label>
                    <div className="relative">
                      <input type="number" min={0} step={100} className="input-base pr-10 text-lg font-semibold"
                        value={tier.price_try ?? 0}
                        onChange={(e) => setTier(s.slug, i, { price_try: e.target.value === "" ? 0 : Number(e.target.value) })}
                        data-testid={`admin-pricing-input-${s.slug}-${tier.id}`} />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink/40">₺</span>
                    </div>
                    <p className="mt-1.5 text-xs text-ink/45">Sitede: <strong className="text-ink/70">{fmt(tier.price_try)} ₺/ay</strong> · {(tier.features_tr || tier.features || []).length} özellik</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
